import type { CSSProperties } from "react";
import type { SpacingKey } from "src/types/emotion";
import { theme } from "src/theme";
import { GridContainer } from "./Grid.styled";

type BreakpointGap = {
  mobile?: SpacingKey;
  tablet?: SpacingKey;
  desktop?: SpacingKey;
};

type Gaps = BreakpointGap | { column?: BreakpointGap; row?: BreakpointGap };

type BreakpointVisibility = {
  mobile?: boolean;
  tablet?: boolean;
  desktop?: boolean;
};

type GridProps = {
  itemsPerColumn?: {
    mobile?: number;
    tablet?: number;
    desktop?: number;
  };
  gap?: Gaps;
  items: React.ReactNode;
  headerElement?: React.ReactNode;
  footerElement?: React.ReactNode;
  hideHeaderOn?: BreakpointVisibility;
  hideFooterOn?: BreakpointVisibility;
};

function resolveSpacing(key: SpacingKey | undefined) {
  return theme.space[key ?? "space0"] || "0px";
}

function resolveVisibilityVars(
  prefix: string,
  hideOn: BreakpointVisibility | undefined
): Record<string, string> {
  return {
    [`--${prefix}-mobile-display`]: hideOn?.mobile ? "none" : "block",
    [`--${prefix}-tablet-display`]: hideOn?.tablet ? "none" : "block",
    [`--${prefix}-desktop-display`]: hideOn?.desktop ? "none" : "block",
  };
}

function resolveGridVars(
  itemsPerColumn: GridProps["itemsPerColumn"],
  gap: GridProps["gap"],
  hideHeaderOn: GridProps["hideHeaderOn"],
  hideFooterOn: GridProps["hideFooterOn"]
): CSSProperties {
  const vars: Record<string, string | number> = {
    "--mobile-columns": itemsPerColumn?.mobile || 2,
    "--tablet-columns": itemsPerColumn?.tablet || 3,
    "--desktop-columns": itemsPerColumn?.desktop || 3,
  };

  if (gap) {
    if ("column" in gap || "row" in gap) {
      const { column, row } = gap as {
        column?: BreakpointGap;
        row?: BreakpointGap;
      };
      vars["--mobile-column-gap"] = resolveSpacing(column?.mobile);
      vars["--tablet-column-gap"] = resolveSpacing(column?.tablet);
      vars["--desktop-column-gap"] = resolveSpacing(column?.desktop);
      vars["--mobile-row-gap"] = resolveSpacing(row?.mobile);
      vars["--tablet-row-gap"] = resolveSpacing(row?.tablet);
      vars["--desktop-row-gap"] = resolveSpacing(row?.desktop);
    } else {
      const flat = gap as BreakpointGap;
      vars["--mobile-column-gap"] = resolveSpacing(flat.mobile);
      vars["--mobile-row-gap"] = resolveSpacing(flat.mobile);
      vars["--tablet-column-gap"] = resolveSpacing(flat.tablet);
      vars["--tablet-row-gap"] = resolveSpacing(flat.tablet);
      vars["--desktop-column-gap"] = resolveSpacing(flat.desktop);
      vars["--desktop-row-gap"] = resolveSpacing(flat.desktop);
    }
  }

  return {
    ...vars,
    ...resolveVisibilityVars("header", hideHeaderOn),
    ...resolveVisibilityVars("footer", hideFooterOn),
  } as CSSProperties;
}

function Grid({
  items,
  headerElement,
  footerElement,
  itemsPerColumn,
  gap,
  hideHeaderOn,
  hideFooterOn,
}: GridProps) {
  return (
    <GridContainer
      style={resolveGridVars(itemsPerColumn, gap, hideHeaderOn, hideFooterOn)}
    >
      {headerElement ? (
        <div className="header-element-wrapper">{headerElement}</div>
      ) : null}
      {items}
      {footerElement ? (
        <div className="footer-element-wrapper">{footerElement}</div>
      ) : null}
    </GridContainer>
  );
}

export { Grid };
